import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    type RevisionInfo,
    type RevisionSemanticChange,
    RevisionSemanticChangeType,
    RevisionType,
} from '@gitbook/hive-core';
import { BadgeIcon, sizes } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { BadgeKind, IconDefinition } from '@gitbook/spine-react';

import { GitSyncProviderIcon } from '../GitSyncProviders';

/**
 * Icon with the user avatar and a badge.
 */
export function HiveRevisionIcon(props: {
    space: api.Space;
    changeRequest: boolean;
    revision: RevisionInfo;
    changes?: RevisionSemanticChange[];
    className?: string;
}): React.ReactElement {
    const { space, changeRequest, revision, changes } = props;
    const size = sizes.AVATAR_M;
    return (
        <BadgeIcon
            {...getBadgeProps(space, changeRequest, revision, changes ?? [])}
            kind="secondary"
            size={size}
        />
    );
}

function getBadgeProps(
    space: api.Space,
    changeRequest: boolean,
    revision: RevisionInfo,
    changes: RevisionSemanticChange[]
): { icon: IconDefinition; kind: BadgeKind } {
    if (revision.type === RevisionType.Rollback) {
        return {
            icon: Icon.RollBack,
            kind: 'merge',
        };
    }

    if (revision.type === RevisionType.Merge) {
        return {
            icon: Icon.ChangeRequestMerged,
            kind: 'merge',
        };
    }

    if (revision.type === RevisionType.Update) {
        return {
            icon: Icon.ChangeRequestMerged,
            kind: 'merge',
        };
    }

    if (revision.git?.commit && !revision.git.commit.createdByGitBook) {
        return {
            icon: space.gitSync?.installationProvider
                ? GitSyncProviderIcon[space.gitSync.installationProvider]
                : Icon.ChangeRequestMerged,
            kind: 'secondary',
        };
    }

    if (changes?.length === 0) {
        // Initial revision of the space or change request
        return {
            icon: changeRequest ? Icon.ChangeRequest : Icon.Space,
            kind: 'info',
        };
    }

    const [change] = changes;

    if (!change) {
        return {
            icon: Icon.Edit,
            kind: 'info',
        };
    }

    switch (change.type) {
        case RevisionSemanticChangeType.PageEdited:
        case RevisionSemanticChangeType.PageCreated:
        case RevisionSemanticChangeType.PageDeleted:
            return {
                icon: Icon.Page,
                kind: 'info',
            };
        case RevisionSemanticChangeType.PageMoved:
            return {
                icon: Icon.ArrowRight,
                kind: 'info',
            };
        case RevisionSemanticChangeType.FileCreated:
        case RevisionSemanticChangeType.FileEdited:
        case RevisionSemanticChangeType.FileDeleted:
            return {
                icon: Icon.Files,
                kind: 'info',
            };
        default:
            return {
                icon: Icon.Edit,
                kind: 'info',
            };
    }
}
